import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CloseButton,
  Dialog,
  Field,
  FieldErrorText,
  FieldLabel,
  FieldRoot,
  Flex,
  HStack,
  Heading,
  Input,
  Menu,
  Portal,
  SegmentGroup,
  Separator,
  Skeleton,
  SelectContent,
  SelectControl,
  SelectIndicator,
  SelectIndicatorGroup,
  SelectItem,
  SelectPositioner,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
  Stack,
  Switch,
  Tabs,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'

import InvoiceSentState from './InvoiceSentState'
import { formatEuro, isUnionInvoiceBank } from '../../helper/invoice.helpers'
import type { FormViewProps, SentViewProps } from './CreateInvoiceDialog.types'
import { LuInfo, LuMailCheck, LuMailPlus, LuSave } from 'react-icons/lu'
import { HiCheck, HiX } from 'react-icons/hi'
import { applyInvoiceNumberToggle } from './invoiceNumberToggle'
import { resolveInvoiceNumberDisplay } from './invoiceNumberPreview'

function ModeSwitch({
  value,
  onChange,
}: {
  value: 'MONTH' | 'DATE_RANGE' | 'ACTIVITY_SELECTION'
  onChange: (value: 'MONTH' | 'DATE_RANGE' | 'ACTIVITY_SELECTION') => void
}) {
  const items = [
    { value: 'MONTH', label: 'Monat' },
    { value: 'DATE_RANGE', label: 'Zeitraum' },
    { value: 'ACTIVITY_SELECTION', label: 'Aktivitäten' },
  ] as const

  return (
    <VStack align="stretch" gap={2} flex={1}>
      <Text>Abrechnungsmodus</Text>

      <SegmentGroup.Root
        value={value}
        onValueChange={details =>
          onChange(details.value as 'MONTH' | 'DATE_RANGE' | 'ACTIVITY_SELECTION')
        }
      >
        <SegmentGroup.Indicator />

        {items.map(item => (
          <SegmentGroup.Item key={item.value} value={item.value} flex="1" minW={0}>
            <SegmentGroup.ItemText textAlign="center" whiteSpace="nowrap">
              {item.label}
            </SegmentGroup.ItemText>
            <SegmentGroup.ItemHiddenInput />
          </SegmentGroup.Item>
        ))}
      </SegmentGroup.Root>
    </VStack>
  )
}
export function InvoiceDialogFormView({
  mode,
  draftInvoice,
  previewGroups,
  formik,
  commentLen,
  monthCollection,
  bankCollection,
  submitting,
  // canCreateOrSend,
  autoInvoiceEnabled,
  // autoInvoiceDayOfMonth,
  invoiceNumberPreview,
  invoiceSettingsComplete,
  invoiceSettingsMissingFields,
  onGoToInvoiceSettings,
  onClose,
  activities,
  // visibleActivities,
  activityFilterTab,
  groupedVisibleActivities,
  setActivityFilterTab,
  isSelected,
  toggleActivity,
  canSubmit,
  run,
}: FormViewProps) {
  const isUnionFlow = isUnionInvoiceBank(formik.values.bankId)

  const invoiceNumberDisplay = resolveInvoiceNumberDisplay({
    mode,
    useCustomInvoiceNumber: !!formik.values.useCustomInvoiceNumber,
    customInvoiceNumber: formik.values.invoiceNumber,
    draftInvoiceNumber: draftInvoice?.invoiceNumber,
    autoNumberingEnabled: !!autoInvoiceEnabled,
    preview: invoiceNumberPreview,
  })

  const emptyStateText = (() => {
    if (formik.values.mode === 'ACTIVITY_SELECTION') {
      return {
        title: 'Keine Aktivitäten',
        description:
          'Für den ausgewählten Auftraggeber liegen keine offenen Aktivitäten vor. Bitte wählen Sie einen anderen Auftraggeber aus, oder erstellen Sie eine neue Aktivität für diesen Auftraggeber.',
      }
    }

    return {
      title: 'Keine Aktivitäten',
      description: 'Für den ausgewählten Zeitraum liegen keine abrechenbaren Aktivitäten vor.',
    }
  })()

  return (
    <>
      <Dialog.Header>
        <Dialog.Title>
          {mode === 'edit' ? 'Entwurf bearbeiten' : 'Rechnung erstellen'}
          {autoInvoiceEnabled && (
            <Badge size="xs" variant="subtle">
              Automatische Rechnungserstellung aktiviert <LuInfo />
            </Badge>
          )}
        </Dialog.Title>

        <Dialog.CloseTrigger asChild>
          <CloseButton />
        </Dialog.CloseTrigger>
      </Dialog.Header>

      <Dialog.Body>
        <Flex
          direction="column"
          overflow="auto"
          css={{
            scrollbarGutter: 'stable',
            '@media (max-width: 767px)': {
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            },
          }}
        >
          <Flex direction="column" gap={5}>
            {invoiceSettingsComplete === false && (
              <Alert.Root status="warning" variant="surface" colorPalette="gray">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Title>Rechnungseinstellungen unvollständig</Alert.Title>
                  <Alert.Description>
                    Bitte füllen Sie Ihre Rechnungseinstellungen unter{' '}
                    <b>Einstellungen → Rechnungen</b> aus, bevor Sie Rechnungen erstellen.
                    {invoiceSettingsMissingFields?.length ? (
                      <>
                        <br />
                        Fehlende Angaben: <b>{invoiceSettingsMissingFields.join(', ')}</b>
                      </>
                    ) : null}
                  </Alert.Description>
                </Alert.Content>

                {onGoToInvoiceSettings && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      onClose()
                      onGoToInvoiceSettings()
                    }}
                  >
                    Zu den Einstellungen
                  </Button>
                )}
              </Alert.Root>
            )}

            <VStack align="stretch" gap={3}>
              <HStack align="center" gap={3} wrap="wrap">
                <Text>Rechnungsnummer:</Text>

                {invoiceNumberDisplay.kind === 'loading' ? (
                  <Skeleton data-testid="invoice-number-preview-loading">
                    <Text>Wird ermittelt…</Text>
                  </Skeleton>
                ) : (
                  <Text data-testid="invoice-number-display">{invoiceNumberDisplay.text}</Text>
                )}

                {invoiceNumberDisplay.kind === 'unavailable' && (
                  <Text textStyle="metadata" color="fg.muted">
                    Vorschau nicht verfügbar
                  </Text>
                )}
              </HStack>

              <Stack direction={{ base: 'column', md: 'row' }} align="center" gap={4}>
                <Input
                  value={formik.values.invoiceNumber ?? ''}
                  onChange={e => formik.setFieldValue('invoiceNumber', e.target.value)}
                  onBlur={() => formik.setFieldTouched('invoiceNumber', true)}
                  placeholder="Andere Rechnungsnummer eintragen"
                  disabled={!formik.values.useCustomInvoiceNumber}
                  width="50%"
                />

                <HStack minW="fit-content">
                  <Switch.Root
                    checked={!!formik.values.useCustomInvoiceNumber}
                    colorPalette="green"
                    onCheckedChange={e => applyInvoiceNumberToggle(formik, !!e.checked)}
                  >
                    <Switch.HiddenInput data-testid="invoice-number-switch" />
                    <Switch.Control>
                      <Switch.Thumb>
                        <Switch.ThumbIndicator fallback={<HiX />}>
                          <HiCheck />
                        </Switch.ThumbIndicator>
                      </Switch.Thumb>
                    </Switch.Control>
                  </Switch.Root>

                  <Text textStyle="body">Andere Rechnungsnummer</Text>
                </HStack>
              </Stack>

              {/* FieldErrorText reads its styling from FieldRoot's context, so an orphaned one
                  throws on render rather than simply looking wrong — it took the whole dialog
                  down. Wrapped here; the invalid flag keeps aria-invalid honest. */}
              <FieldRoot invalid={!!formik.touched.invoiceNumber && !!formik.errors.invoiceNumber}>
                <FieldErrorText>
                  {formik.touched.invoiceNumber && formik.errors.invoiceNumber}
                </FieldErrorText>
              </FieldRoot>

              {formik.values.useCustomInvoiceNumber && (
                <Text textStyle="metadata" color="fg.muted">
                  <Text as="span" textStyle="xsMedium">
                    Empfehlung:
                  </Text>{' '}
                  Verwenden Sie die automatisch generierte fortlaufende Rechnungsnummer und
                  hinterlegen Sie Ihre Kunden im SIB-Portal unter Einstellungen → Auftraggeber.
                  Dadurch wird die Abrechnung deutlich einfacher und Sie vermeiden zusätzliche
                  Tabellen und manuelle Dokumentation.
                </Text>
              )}
            </VStack>

            <Stack direction={{ base: 'column', md: 'row' }} gap={6} align="flex-start">
              <FieldRoot flex={1} invalid={!!formik.errors.bankId && !!formik.touched.bankId}>
                <FieldLabel>
                  Auftraggeber <Field.RequiredIndicator />
                </FieldLabel>

                <SelectRoot
                  collection={bankCollection}
                  disabled={!bankCollection.items.length}
                  value={[String(formik.values.bankId)]}
                  onValueChange={v => formik.setFieldValue('bankId', Number(v.value[0]))}
                >
                  <SelectControl>
                    <SelectTrigger>
                      <SelectValueText placeholder="Auftraggeber wählen" />
                      <SelectIndicatorGroup>
                        <SelectIndicator />
                      </SelectIndicatorGroup>
                    </SelectTrigger>
                  </SelectControl>

                  <Portal>
                    <SelectPositioner style={{ zIndex: 10000 }}>
                      <SelectContent>
                        {bankCollection.items.map(it => (
                          <SelectItem key={it.value} item={it.value}>
                            {it.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </SelectPositioner>
                  </Portal>
                </SelectRoot>

                <Text mt={1} textStyle="metadata" color="fg.muted">
                  Welchen Auftraggeber möchten Sie abrechnen?
                </Text>

                <FieldErrorText>{formik.touched.bankId && formik.errors.bankId}</FieldErrorText>
              </FieldRoot>

              <ModeSwitch
                value={formik.values.mode}
                onChange={value => formik.setFieldValue('mode', value)}
              />
            </Stack>

            {formik.values.mode === 'MONTH' && (
              <FieldRoot invalid={!!formik.errors.billingMonth && !!formik.touched.billingMonth}>
                <FieldLabel>
                  Abrechnungsmonat{' '}
                  <Text as="span" color="fg.error">
                    *
                  </Text>
                </FieldLabel>

                <SelectRoot
                  collection={monthCollection}
                  value={[String(formik.values.billingMonth)]}
                  onValueChange={v => formik.setFieldValue('billingMonth', Number(v.value[0]))}
                >
                  <SelectControl>
                    <SelectTrigger>
                      <SelectValueText placeholder="Monat wählen" />
                      <SelectIndicatorGroup>
                        <SelectIndicator />
                      </SelectIndicatorGroup>
                    </SelectTrigger>
                  </SelectControl>

                  <Portal>
                    <SelectPositioner style={{ zIndex: 10000 }}>
                      <SelectContent>
                        {monthCollection.items.map(it => (
                          <SelectItem key={it.value} item={it.value}>
                            {it.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </SelectPositioner>
                  </Portal>
                </SelectRoot>

                <FieldErrorText>
                  {formik.touched.billingMonth && formik.errors.billingMonth}
                </FieldErrorText>
              </FieldRoot>
            )}

            {formik.values.mode === 'DATE_RANGE' && (
              <Stack direction={{ base: 'column', md: 'row' }} align="flex-start" gap={6}>
                <FieldRoot
                  flex={1}
                  invalid={!!formik.errors.periodStart && !!formik.touched.periodStart}
                >
                  <FieldLabel>
                    Zeitraum von{' '}
                    <Text as="span" color="fg.error">
                      *
                    </Text>
                  </FieldLabel>
                  <Input
                    type="date"
                    value={formik.values.periodStart ?? ''}
                    onChange={e => formik.setFieldValue('periodStart', e.target.value)}
                    onBlur={() => formik.setFieldTouched('periodStart', true)}
                  />
                  <FieldErrorText>
                    {formik.touched.periodStart && formik.errors.periodStart}
                  </FieldErrorText>
                </FieldRoot>

                <FieldRoot
                  flex={1}
                  invalid={!!formik.errors.periodEnd && !!formik.touched.periodEnd}
                >
                  <FieldLabel>
                    Bis{' '}
                    <Text as="span" color="fg.error">
                      *
                    </Text>
                  </FieldLabel>
                  <Input
                    type="date"
                    value={formik.values.periodEnd ?? ''}
                    onChange={e => formik.setFieldValue('periodEnd', e.target.value)}
                    onBlur={() => formik.setFieldTouched('periodEnd', true)}
                  />
                  <FieldErrorText>
                    {formik.touched.periodEnd && formik.errors.periodEnd}
                  </FieldErrorText>
                </FieldRoot>
              </Stack>
            )}

            {activities.length === 0 && (
              <Alert.Root status="info" variant="subtle">
                <Alert.Indicator bgSize="xs" />
                <Alert.Content>
                  <Alert.Title>{emptyStateText.title}</Alert.Title>
                  <Alert.Description>{emptyStateText.description}</Alert.Description>
                </Alert.Content>
              </Alert.Root>
            )}

            {formik.values.mode === 'ACTIVITY_SELECTION' && (
              <VStack align="stretch" gap={4}>
                <Tabs.Root
                  value={activityFilterTab}
                  onValueChange={details => setActivityFilterTab(details.value as any)}
                  variant="subtle"
                >
                  <Tabs.List>
                    {[
                      { key: 'ALL', label: 'Alle Aktivitäten' },
                      { key: 'SELECTED', label: 'Ausgewählt' },
                      { key: 'UNSELECTED', label: 'Nicht ausgewählt' },
                    ].map(tab => (
                      <Tabs.Trigger key={tab.key} value={tab.key}>
                        {tab.label}
                      </Tabs.Trigger>
                    ))}
                  </Tabs.List>
                </Tabs.Root>

                {activities.length > 0 &&
                (formik.values.selectedActivityIds?.length ?? 0) === 0 ? (
                  <Alert.Root status="info" variant="subtle">
                    <Alert.Indicator />
                    <Alert.Content>
                      <Alert.Title>Keine Aktivitäten ausgewählt</Alert.Title>
                      <Alert.Description>
                        Bitte wählen Sie mindestens eine Aktivität aus der Liste aus.
                      </Alert.Description>
                    </Alert.Content>
                  </Alert.Root>
                ) : null}
                <VStack align="stretch" gap={5}>
                  {groupedVisibleActivities.map(([date, items]) => (
                    <Box key={date}>
                      <Text>{new Date(date).toLocaleDateString('de-DE')}</Text>

                      <VStack align="stretch" gap={3}>
                        {items.map(activity => {
                          const selected = isSelected(activity.id)

                          return (
                            <Card.Root
                              key={activity.id}
                              variant={selected ? 'subtle' : 'outline'}
                              colorPalette="gray"
                              cursor="pointer"
                              onClick={() => toggleActivity(activity.id)}
                            >
                              <Card.Body>
                                <Flex justify="space-between" align="flex-start" gap={4}>
                                  <VStack align="stretch" gap={2} flex={1}>
                                    <Card.Title>
                                      {activity.amount} &nbsp; &nbsp;{activity.title}
                                    </Card.Title>

                                    {activity.internalComment ? (
                                      <Card.Description>
                                        {activity.internalComment}
                                      </Card.Description>
                                    ) : null}
                                  </VStack>
                                </Flex>
                              </Card.Body>
                            </Card.Root>
                          )
                        })}
                      </VStack>
                    </Box>
                  ))}
                </VStack>

                <FieldRoot
                  invalid={
                    !!formik.touched.selectedActivityIds && !!formik.errors.selectedActivityIds
                  }
                >
                  <FieldErrorText>
                    {formik.touched.selectedActivityIds &&
                      (formik.errors.selectedActivityIds as string)}
                  </FieldErrorText>
                </FieldRoot>
              </VStack>
            )}

            <FieldRoot invalid={!!formik.errors.comment && !!formik.touched.comment}>
              <FieldLabel>
                Kommentar an Auftraggeber <Badge size="xs">optional</Badge>
              </FieldLabel>

              <Textarea
                resize="vertical"
                value={formik.values.comment ?? ''}
                onChange={e => formik.setFieldValue('comment', e.target.value)}
                onBlur={() => formik.setFieldTouched('comment', true)}
              />

              <Text textStyle="metadata" color="fg.muted" mt={1}>
                Kommentar an Auftraggeber auf Rechnung hinzufügen.
              </Text>
              <Text textStyle="metadata" color="fg.muted">
                {commentLen} von 3000 Zeichen.
              </Text>

              <FieldErrorText>{formik.touched.comment && formik.errors.comment}</FieldErrorText>
            </FieldRoot>

            {mode === 'edit' && draftInvoice && (
              <>
                <Separator />
                <Box>
                  <Heading size="xl" mb={6}>
                    Zusammenfassung
                  </Heading>

                  <VStack align="stretch" gap={0}>
                    {previewGroups.map(g => (
                      <Box key={g.group}>
                        <Flex w="full" justify="space-between" align="stretch" gap={6} my={2}>
                          <Flex
                            direction="column"
                            justify="space-between"
                            align="flex-start"
                            flex={1}
                          >
                            <Text textStyle="body">{g.group}</Text>
                            <Text textStyle="financialValue">{formatEuro(g.subtotalCents)}</Text>
                          </Flex>

                          <Box flex={2} w="full">
                            <Box
                              display="grid"
                              gridTemplateColumns={{
                                base: 'minmax(1.5rem, 1fr) minmax(2.5rem, 2fr)',
                                sm: 'minmax(1.5rem, 1fr) minmax(2.5rem, 1fr)',
                              }}
                              gap={2}
                              fontSize="sm"
                              mb={2}
                            >
                              <Text textAlign="right">Menge</Text>
                              <Text textAlign="right" whiteSpace="nowrap">
                                Preis pro Einheit
                              </Text>
                            </Box>

                            <VStack align="stretch" gap={0}>
                              {g.lines.map(line => (
                                <Box
                                  key={line.id}
                                  display="grid"
                                  gridTemplateColumns={{
                                    base: 'minmax(1.5rem, 1fr) minmax(2.5rem, 2fr)',
                                    sm: 'minmax(1.5rem, 1fr) minmax(2.5rem, 1fr)',
                                  }}
                                  gap={2}
                                  fontSize="sm"
                                >
                                  <Text textAlign="right" whiteSpace="nowrap">
                                    {line.menge}
                                  </Text>
                                  <Text textAlign="right" whiteSpace="nowrap">
                                    {formatEuro(line.preisProEinheitCents)}
                                  </Text>
                                </Box>
                              ))}
                            </VStack>
                          </Box>
                        </Flex>

                        <Separator />
                      </Box>
                    ))}
                  </VStack>
                </Box>
              </>
            )}
          </Flex>
        </Flex>
      </Dialog.Body>

      <Dialog.Footer>
        <Button variant="ghost" onClick={onClose}>
          Abbrechen
        </Button>

        <Menu.Root
          positioning={{ strategy: 'fixed', hideWhenDetached: true, placement: 'top-end' }}
        >
          <Menu.Trigger asChild>
            <Button
              colorPalette="blue"
              variant="outline"
              disabled={submitting !== null || !canSubmit}
              loading={submitting !== null}
            >
              Speichern
            </Button>
          </Menu.Trigger>

          <Menu.Positioner>
            <Menu.Content minW="260px">
              <Menu.Item value="preview" onSelect={() => run('preview')}>
                <HStack gap={3}>
                  <LuSave />
                  <Text>Vorschau speichern</Text>
                </HStack>
              </Menu.Item>

              {isUnionFlow ? (
                <Menu.Item value="submit" onSelect={() => run('submit')}>
                  <HStack gap={3}>
                    <LuMailPlus />
                    <Text>Rechnung prüfen lassen</Text>
                  </HStack>
                </Menu.Item>
              ) : (
                <>
                  <Menu.Item value="create" onSelect={() => run('create')}>
                    <HStack gap={3}>
                      <LuMailPlus />
                      <Text>Rechnung erstellen</Text>
                    </HStack>
                  </Menu.Item>

                  <Menu.Item value="create_and_send" onSelect={() => run('create_and_send')}>
                    <HStack gap={3}>
                      <LuMailCheck />
                      <Text>Rechnung erstellen &amp; versenden</Text>
                    </HStack>
                  </Menu.Item>
                </>
              )}
            </Menu.Content>
          </Menu.Positioner>
        </Menu.Root>
      </Dialog.Footer>
    </>
  )
}

export function InvoiceDialogSentView({
  mode,
  sentInvoiceId,
  onClose,
  onGoToInvoices,
  onDownloadCreatedInvoice,
}: SentViewProps) {
  return (
    <>
      <Dialog.Header>
        <Dialog.Title>
          {mode === 'edit' ? 'Entwurf bearbeiten' : 'Rechnung erstellen'}
        </Dialog.Title>

        <Dialog.CloseTrigger asChild>
          <CloseButton />
        </Dialog.CloseTrigger>
      </Dialog.Header>

      <Dialog.Body>
        <InvoiceSentState
          onGoToInvoices={() => {
            onClose()
            onGoToInvoices?.()
          }}
          onDownload={() => onDownloadCreatedInvoice?.(sentInvoiceId)}
        />
      </Dialog.Body>
    </>
  )
}
